/**
 * Mirrors the storefront's `Project` type
 * (`store/types/Project.ts`) so the admin can preview Liquid templates that
 * read `project.*` variables. Kept local because it lives in a separate
 * package and we don't want a hard dependency between the two repos.
 */
enum ProjectType {
  Grades = 0,
  Units = 1,
  Open = 2,
  Deduction = 3,
  Meal = 4,
}

interface ProjectGrade {
  id: number;
  order: number;
  title: string;
  amount: number;
  deductionPeriod: number | null;
  deductionEndTime: string | null;
  numberOfBeneficiariesNames: number;
}

interface ProjectBankAccount {
  iban: string;
  bankName: string;
  bankAccountId: number;
  bankAccountName: string;
}

interface Project {
  id: string;
  slug?: string;
  linkedGiftingProjectId: string;
  title: string;
  projectDetails: string;
  projectDescriptionImagesUrls: string[];
  shortDescription: string;
  displayProgress: boolean;
  progress: number;
  allowCustomAmount: boolean;
  projectType: ProjectType;
  projectGrades: ProjectGrade[];
  projectUnitValue: number;
  imageUrl: string;
  imageAlt?: string;
  shareOnTwitter: boolean;
  shareOnFacebook: boolean;
  shareOnInstagram: boolean;
  shareOnWhatsApp: boolean;
  allRequiredAmount: number;
  currentPaidAmount: number;
  projectBankAccountShowingOption: number;
  projectBankAccounts: ProjectBankAccount[];
  seoTitle: string;
  seoDescription: string;
  seoKeyword: string;
  isFastPaymentActive: boolean;
  projectVisualEffect: boolean;
  bannerImageFullUrl: string;
  allowOpenDonation: boolean;
  minimumOpenDonationAmount: number;
  isOnBehalfOnProject: boolean;
  hasEndowmentCertificate: boolean;
  themeUrl: string;
}

export const mockProject: Project = {
  id: "preview-project",
  slug: "preview-project",
  linkedGiftingProjectId: "",
  title: "اسم المشروع التجريبي",
  projectDetails:
    "<p>هذا وصف تجريبي للمشروع. يمكنك تخصيص هذا النص من لوحة التحكم. سيظهر هنا الوصف الفعلي للمشروع عند ربط الثيم بصفحة المشروع.</p>",
  projectDescriptionImagesUrls: ["https://placehold.net/900x500.png"],
  shortDescription: "ملخص قصير عن المشروع التجريبي يظهر في البطاقات والمعاينة.",
  displayProgress: true,
  progress: 65,
  allowCustomAmount: true,
  projectType: ProjectType.Grades,
  projectGrades: [
    {
      id: 1,
      order: 0,
      title: "كفالة شهرية",
      amount: 250,
      deductionPeriod: null,
      deductionEndTime: null,
      numberOfBeneficiariesNames: 1,
    },
    {
      id: 2,
      order: 1,
      title: "كفالة سنوية",
      amount: 3000,
      deductionPeriod: null,
      deductionEndTime: null,
      numberOfBeneficiariesNames: 1,
    },
    {
      id: 3,
      order: 2,
      title: "كفالة كاملة",
      amount: 5000,
      deductionPeriod: null,
      deductionEndTime: null,
      numberOfBeneficiariesNames: 2,
    },
  ],
  projectUnitValue: 100,
  imageUrl: "https://placehold.net/600x400.png",
  imageAlt: "صورة المشروع",
  shareOnTwitter: true,
  shareOnFacebook: true,
  shareOnInstagram: true,
  shareOnWhatsApp: true,
  allRequiredAmount: 100000,
  currentPaidAmount: 65000,
  projectBankAccountShowingOption: 1,
  projectBankAccounts: [
    {
      iban: "SA0000000000000000000000",
      bankName: "البنك التجريبي",
      bankAccountId: 1,
      bankAccountName: "حساب المشروع",
    },
  ],
  seoTitle: "عنوان SEO تجريبي",
  seoDescription: "وصف SEO تجريبي للمشروع",
  seoKeyword: "تبرع، خير، مشروع",
  isFastPaymentActive: true,
  projectVisualEffect: false,
  bannerImageFullUrl: "",
  allowOpenDonation: true,
  minimumOpenDonationAmount: 50,
  isOnBehalfOnProject: false,
  hasEndowmentCertificate: false,
  themeUrl: "",
};

/**
 * Last-resort string replacement for Liquid placeholders. Used as a fallback
 * if the Liquid engine fails to parse the template — the admin still sees
 * something readable instead of raw `{{ project.title }}` braces in the
 * iframe.
 */
const FALLBACK_REPLACEMENTS: Record<string, string> = {
  title: mockProject.title,
  projectDetails: mockProject.projectDetails,
  shortDescription: mockProject.shortDescription,
  imageUrl: mockProject.imageUrl,
  imageAlt: mockProject.imageAlt ?? mockProject.title,
  bannerImageFullUrl: mockProject.bannerImageFullUrl,
  progress: String(mockProject.progress),
  displayProgress: String(mockProject.displayProgress),
  projectType: String(mockProject.projectType),
  allowOpenDonation: String(mockProject.allowOpenDonation),
  allowCustomAmount: String(mockProject.allowCustomAmount),
  allRequiredAmount: String(mockProject.allRequiredAmount),
  currentPaidAmount: String(mockProject.currentPaidAmount),
  minimumOpenDonationAmount: String(mockProject.minimumOpenDonationAmount),
  projectBankAccountShowingOption: String(
    mockProject.projectBankAccountShowingOption,
  ),
  shareOnTwitter: String(mockProject.shareOnTwitter),
  shareOnFacebook: String(mockProject.shareOnFacebook),
  shareOnInstagram: String(mockProject.shareOnInstagram),
  shareOnWhatsApp: String(mockProject.shareOnWhatsApp),
  isFastPaymentActive: String(mockProject.isFastPaymentActive),
  hasEndowmentCertificate: String(mockProject.hasEndowmentCertificate),
  isOnBehalfOnProject: String(mockProject.isOnBehalfOnProject),
  seoTitle: mockProject.seoTitle,
  seoDescription: mockProject.seoDescription,
  seoKeyword: mockProject.seoKeyword,
};

/**
 * Mirrors the storefront's `useProjectDetailsBinding` for the preview iframe.
 * The iframe has no React controller, so client-fetched custom tags would stay
 * empty. This helper performs the same three transforms against the rendered
 * HTML string before injection:
 *
 *   1. Fill `<khzama-project-details></khzama-project-details>` with the body.
 *   2. Set `<khzama-project-description-image> img[src]` to the image URL.
 *   3. Unhide `[data-show-when="has-project-details"]` wrappers when content
 *      is non-empty (matches storefront — empty projects stay hidden).
 */
export const injectMockProjectDetails = (
  html: string,
  opts: { projectDetails?: string | null; imageUrl?: string | null },
): string => {
  const body = opts.projectDetails ?? "";
  const img = opts.imageUrl ?? "";
  const hasContent = Boolean(body) || Boolean(img);

  let out = html;

  out = out.replace(
    /<khzama-project-details>\s*<\/khzama-project-details>/gi,
    `<khzama-project-details>${body}</khzama-project-details>`,
  );

  out = out.replace(
    /(<khzama-project-description-image\b[^>]*>[\s\S]*?<img\b)([^>]*?)(\s*\/?>)/gi,
    (_m, open: string, attrs: string, close: string) => {
      const stripped = attrs.replace(/\s+src="[^"]*"/i, "");
      return `${open}${stripped} src="${img}"${close}`;
    },
  );

  if (hasContent) {
    out = out.replace(
      /(<[^>]*\bdata-show-when="has-project-details"[^>]*?)\s+hidden\b/gi,
      "$1",
    );
  }

  return out;
};

export const replaceLiquidVariables = (raw: string): string => {
  let out = raw;
  // Replace {{ project.field }} or {{ field }} (with optional whitespace and
  // filters) with mock values. Strips a leading `project.` so both styles
  // share the same lookup table.
  out = out.replace(
    /\{\{\s*([\w.]+)(?:\s*\|[^}]*)?\s*\}\}/g,
    (match, key: string) => {
      const lookupKey = key.replace(/^project\./, "");
      if (lookupKey in FALLBACK_REPLACEMENTS)
        return FALLBACK_REPLACEMENTS[lookupKey];
      return match;
    },
  );
  // Strip Liquid control tags ({% ... %}) so they don't render as text.
  out = out.replace(/\{%[\s\S]*?%\}/g, "");
  return out;
};

/**
 * Wraps the rendered template in a clean, isolated HTML document with the
 * fonts and viewport meta the storefront uses. The iframe gets this string
 * via `srcDoc`, so it runs sandboxed without inheriting any admin styles.
 */
export const preparePreviewHtml = (
  renderedBody: string,
): string => `<!DOCTYPE html>
<html dir="rtl" lang="ar">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Theme Preview</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&family=Tajawal:wght@400;500;700&display=swap"
      rel="stylesheet"
    />
    <base target="_blank" />
    <style>
      *, *::before, *::after { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body {
        font-family: "Cairo", "Tajawal", system-ui, sans-serif;
        line-height: 1.6;
        // min-height: 50vh;
      }
      img { max-width: 100%; height: auto; display: block; }
      a { color: inherit; }
      khzama-project-details, khzama-project-description-image { display: block; }
    </style>
  </head>
  <body>
${renderedBody}
  </body>
</html>`;
